import { tokenStorage, readCsrfCookie } from './storage';

/**
 * Thin fetch wrapper with three responsibilities:
 *  1. Inject the bearer access token from storage on every call.
 *  2. On 401, attempt a single refresh; on success retry the original
 *     request, on failure clear tokens and emit `auth:logout`.
 *  3. Normalise errors into `ApiError` so callers can `instanceof` check
 *     and React Query can attach typed error info.
 *
 * The wrapper does NOT know about React state — `AuthProvider` listens
 * to `auth:logout` and triggers the redirect. This keeps the client
 * usable from anywhere (loaders, mutations, plain code) without a hook.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    readonly details: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}${code ? ` (${code})` : ''}`);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Auto-JSON.stringified if not already a string / FormData. */
  body?: unknown;
  /** Skip the bearer token (used by the login endpoint itself). */
  skipAuth?: boolean;
}

interface SessionResponse {
  accessToken: string;
  expiresInSeconds: number;
}

/** Single in-flight refresh promise — multiple parallel 401s share it. */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Broadcast the logout event so the AuthProvider can react and force a
 * redirect to /login. We use a CustomEvent rather than a callback so the
 * api-client stays React-agnostic.
 */
function emitLogout(reason: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason } }));
}

/**
 * Refresh the access token using the HttpOnly cookie. We never send the
 * refresh token in the body — the cookie travels automatically. CSRF
 * defence: read the double-submit cookie and echo it in the header so
 * the server can verify the request came from a browser that received
 * the session, not a cross-origin attacker.
 *
 * Returns the new access token on success; null on any failure (no
 * cookie / revoked / expired / CSRF mismatch / network). Callers treat
 * null as "session is dead — log the user out".
 */
async function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<string | null> => {
    const csrf = readCsrfCookie();
    if (!csrf) return null;
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'x-csrf-token': csrf },
      });
      if (!res.ok) return null;
      const json = (await res.json()) as SessionResponse;
      tokenStorage.write({ accessToken: json.accessToken });
      return json.accessToken;
    } catch {
      return null;
    } finally {
      // Reset on next tick so concurrent callers awaiting on this promise
      // still resolve with the same value before a new refresh can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/** Boot-time refresh — used on page load to mint an access token from a still-live cookie. */
export async function bootstrapSession(): Promise<string | null> {
  return tryRefresh();
}

async function performFetch(
  path: string,
  opts: RequestOptions,
  token: string | null,
): Promise<Response> {
  const headers = new Headers(opts.headers);
  if (!headers.has('content-type') && opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  if (token && !opts.skipAuth) {
    headers.set('authorization', `Bearer ${token}`);
  }

  const body =
    opts.body === undefined || typeof opts.body === 'string' || opts.body instanceof FormData
      ? (opts.body as BodyInit | null | undefined)
      : JSON.stringify(opts.body);

  return fetch(path, { ...opts, headers, body });
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: { code?: string; message?: string; details?: unknown } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    // Body wasn't JSON — fall through with empty payload
  }
  return new ApiError(res.status, payload.code ?? null, payload.details, payload.message);
}

/**
 * Internal — runs the request through the bearer + single-flight refresh
 * dance and returns a successful `Response` (throws `ApiError` for any
 * non-2xx that survives the refresh). Body-shape-agnostic: callers parse
 * JSON / Blob / text themselves.
 */
async function authedFetch(path: string, opts: RequestOptions): Promise<Response> {
  const { accessToken } = tokenStorage.read();
  let res = await performFetch(path, opts, accessToken);

  if (res.status === 401 && !opts.skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await performFetch(path, opts, refreshed);
    } else {
      tokenStorage.clear();
      emitLogout('refresh-failed');
      throw await parseError(res);
    }
  }

  if (!res.ok) {
    throw await parseError(res);
  }
  return res;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await authedFetch(path, opts);
  // Handle 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * GET a binary payload (PDF, image, …) through the same auth + refresh
 * pipeline as `apiFetch`. Used by the receipt download / share actions
 * which need the raw bytes — the receipt endpoint is auth-gated, so a
 * plain `<a href>` or `window.open` would 401.
 */
export async function apiBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const res = await authedFetch(path, { ...opts, method: opts.method ?? 'GET' });
  return await res.blob();
}

/**
 * GET a text payload (HTML, plain text) through the same auth + refresh
 * pipeline. Used by the receipt-print flow: fetch the HTML body, then
 * inject into a popup or hidden iframe to call `window.print()`.
 */
export async function apiText(path: string, opts: RequestOptions = {}): Promise<string> {
  const res = await authedFetch(path, { ...opts, method: opts.method ?? 'GET' });
  return await res.text();
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
  blob: apiBlob,
  text: apiText,
};
